import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Header from '../Header/Header';
import './ContactPage.css';

const ContactPage = () => {
    const { t } = useTranslation();
    const formRef = useRef();

    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [contactStatus, setContactStatus] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setContactForm(prev => ({ ...prev, [name]: value }));
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setContactStatus('');

        try {
            await emailjs.sendForm(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                formRef.current,
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            );

            setContactStatus('success');
            setContactForm({ name: '', email: '', subject: '', message: '' });
        } catch (error) {
            console.error('EmailJS error:', error);
            setContactStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page">
            <Header />

            <div className="contact-page-container">
                <div className="contact-page-card">
                    {/* Header Section */}
                    <div className="contact-page-header">
                        <h1 className="contact-page-title">{t('contactPage.title')}</h1>
                        <p className="contact-page-subtitle">{t('contactPage.subtitle')}</p>
                    </div>

                    <div className="contact-page-content">
                        {/* Left: Contact Info */}
                        <div className="contact-page-info">
                            <div className="contact-info-block">
                                <div className="contact-info-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                        <circle cx="12" cy="10" r="3"/>
                                    </svg>
                                </div>
                                <div className="contact-info-text">
                                    <h3>{t('contactPage.addressTitle')}</h3>
                                    <p>{t('contactSection.address')}</p>
                                </div>
                            </div>

                            <div className="contact-info-block">
                                <div className="contact-info-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                    </svg>
                                </div>
                                <div className="contact-info-text">
                                    <h3>{t('contactPage.phoneTitle')}</h3>
                                    <p>{t('contactSection.phone')}</p>
                                </div>
                            </div>

                            <div className="contact-info-block">
                                <div className="contact-info-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                        <polyline points="22,6 12,13 2,6"/>
                                    </svg>
                                </div>
                                <div className="contact-info-text">
                                    <h3>{t('contactPage.emailTitle')}</h3>
                                    <p>{t('contactSection.email')}</p>
                                </div>
                            </div>

                            <div className="contact-social-section">
                                <h3>{t('contactPage.followUs')}</h3>
                                <div className="contact-social-icons">
                                    <a href="https://www.instagram.com/tafelberg_nuernberg/" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                        </svg>
                                    </a>
                                </div>
                            </div>

                            <Link to="/" className="contact-back-link">
                                ← {t('contactPage.backHome')}
                            </Link>
                        </div>

                        {/* Right: Contact Form */}
                        <div className="contact-page-form-section">
                            <h2>{t('contactPage.formTitle')}</h2>
                            <form ref={formRef} onSubmit={handleContactSubmit} className="contact-page-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="name">{t('contactSection.formName')}</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={contactForm.name}
                                            onChange={handleContactChange}
                                            placeholder={t('contactPage.namePlaceholder')}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="email">{t('contactSection.formEmail')}</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={contactForm.email}
                                            onChange={handleContactChange}
                                            placeholder={t('contactPage.emailPlaceholder')}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="subject">{t('contactPage.subjectLabel')}</label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={contactForm.subject}
                                        onChange={handleContactChange}
                                        placeholder={t('contactPage.subjectPlaceholder')}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="message">{t('contactSection.formMessage')}</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={contactForm.message}
                                        onChange={handleContactChange}
                                        placeholder={t('contactPage.messagePlaceholder')}
                                        rows="6"
                                        required
                                    />
                                </div>

                                <button type="submit" className="contact-submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner"></span>
                                            {t('contactSection.formSending')}
                                        </>
                                    ) : (
                                        <>
                                            {t('contactSection.formSend')}

                                        </>
                                    )}
                                </button>
                            </form>

                            {contactStatus === 'success' && (
                                <div className="contact-page-status success">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                        <polyline points="22 4 12 14.01 9 11.01"/>
                                    </svg>
                                    {t('contactSection.formSuccess')}
                                </div>
                            )}
                            {contactStatus === 'error' && (
                                <div className="contact-page-status error">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="15" y1="9" x2="9" y2="15"/>
                                        <line x1="9" y1="9" x2="15" y2="15"/>
                                    </svg>
                                    {t('contactSection.formError')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
